import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, MapPin, Navigation, Play, Pause, Package, Phone, LogOut, Radio, CheckCircle2, Clock, IndianRupee, ArrowRight, AlertCircle, User, Camera, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { myLoads } from '../mock/mockData';
import { useI18n } from '../lib/i18n';
import { trackingApi, tripsApi, getUser, logout as doLogout } from '../lib/api';
import LanguageToggle from '../components/LanguageToggle';
import PodUploadDialog from '../components/PodUploadDialog';
import DriverEarnings from '../components/DriverEarnings';
import ReturnLoadMatch from '../components/ReturnLoadMatch';
import LoadChat from '../components/LoadChat';

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export default function DriverApp() {
  const navigate = useNavigate();
  const [sharing, setSharing] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [lastLoc, setLastLoc] = useState(null);
  const [error, setError] = useState('');
  const [statusDialog, setStatusDialog] = useState({ open: false, load: null, newStatus: '' });
  const [note, setNote] = useState('');
  const [trips, setTrips] = useState(myLoads);
  const [activeTrip, setActiveTrip] = useState(myLoads.find(l => l.status === 'In Transit') || myLoads[0]);
  const [podOpen, setPodOpen] = useState(false);
  const [earningsOpen, setEarningsOpen] = useState(false);
  const [returnMatchOpen, setReturnMatchOpen] = useState(false);
  const [deliveredLoad, setDeliveredLoad] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  // Redirect to /driver/login if not authenticated
  useEffect(() => {
    if (!localStorage.getItem('ezload_token')) navigate('/driver/login');
  }, [navigate]);

  const stored = getUser();
  const driverName = stored?.name || 'Sohan Lal';
  const initials = driverName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const startSharing = () => {
    if (!navigator.geolocation) { setError('GPS not available on this device'); return; }
    if (!activeTrip) { setError('No active trip'); return; }
    setError('');
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude, speed: pos.coords.speed, heading: pos.coords.heading, ts: Date.now() };
        setLastLoc(loc);
        // Push to backend (silent fail if not logged in)
        trackingApi.update({ load_id: activeTrip.id, lat: loc.lat, lng: loc.lng, speed: loc.speed, heading: loc.heading }).catch(() => {});
      },
      (err) => { setError('GPS permission denied: ' + err.message); setSharing(false); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    setWatchId(id);
    setSharing(true);
    toast({ title: 'Live sharing started', description: 'Your location is being sent to the shipper.' });
  };

  const stopSharing = () => {
    if (watchId != null) navigator.geolocation.clearWatch(watchId);
    setWatchId(null); setSharing(false);
    toast({ title: 'Sharing paused', description: 'Location updates stopped.' });
  };

  useEffect(() => () => { if (watchId != null) navigator.geolocation.clearWatch(watchId); }, [watchId]);

  const updateStatus = async () => {
    const { load, newStatus } = statusDialog;
    try { await tripsApi.updateStatus({ load_id: load.id, status: newStatus, note }); } catch (e) {}
    setTrips(trips.map(t => t.id === load.id ? { ...t, status: newStatus === 'in_transit' ? 'In Transit' : newStatus === 'delivered' ? 'Delivered' : newStatus === 'loading' ? 'Loading' : t.status } : t));
    setStatusDialog({ open: false, load: null, newStatus: '' });
    setNote('');
    toast({ title: 'Status updated', description: `Load ${load.id} → ${newStatus.replace('_', ' ').toUpperCase()}` });
    // Auto-suggest return load on delivery
    if (newStatus === 'delivered') {
      setDeliveredLoad(load);
      setTimeout(() => setReturnMatchOpen(true), 600);
    }
  };

  const statusColor = (s) => s === 'In Transit' ? 'bg-[#00A651]/10 text-[#00A651]' : s === 'Delivered' ? 'bg-blue-50 text-blue-600' : s === 'Loading' ? 'bg-[#FFB800]/15 text-[#B37800]' : 'bg-gray-100 text-gray-600';

  return (
    <div className="min-h-screen bg-[#F7FAF9] pb-24">
      {/* Header */}
      <header className="bg-[#0B2545] text-white sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center">
            <Truck className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <p className="font-bold text-sm leading-tight">EZLOAD Driver</p>
            <p className="text-[11px] text-white/70 leading-tight">{driverName}</p>
          </div>
          <LanguageToggle compact />
          <button onClick={() => { doLogout(); navigate('/'); }} className="p-2 rounded hover:bg-white/10">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Live sharing card */}
      <div className="px-4 py-4">
        <div className={`rounded-2xl p-4 relative overflow-hidden ${sharing ? 'bg-gradient-to-br from-[#00A651] to-[#00c060] text-white' : 'bg-white border border-gray-100'}`}>
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${sharing ? 'bg-white/20' : 'bg-[#00A651]/10'}`}>
              <Radio className={`w-5 h-5 ${sharing ? 'text-white animate-pulse' : 'text-[#00A651]'}`} />
            </div>
            <div className="flex-1">
              <p className={`font-bold ${sharing ? 'text-white' : 'text-[#0B2545]'}`}>{sharing ? 'Live location sharing ON' : 'Share your live location'}</p>
              <p className={`text-xs ${sharing ? 'text-white/80' : 'text-gray-500'}`}>
                {sharing ? (lastLoc ? `${lastLoc.lat.toFixed(4)}, ${lastLoc.lng.toFixed(4)}` : 'Getting GPS...') : 'Shipper will see your truck in real-time on the map.'}
              </p>
              {activeTrip && !sharing && <p className="text-[10px] text-gray-400 mt-1">Trip: {activeTrip.id} • {activeTrip.origin.split(',')[0]} → {activeTrip.destination.split(',')[0]}</p>}
            </div>
          </div>
          {error && <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</p>}
          <Button onClick={sharing ? stopSharing : startSharing} className={`w-full mt-3 h-11 ${sharing ? 'bg-white text-[#00A651] hover:bg-white/90' : 'bg-[#00A651] hover:bg-[#008a43] text-white'}`}>
            {sharing ? <><Pause className="w-4 h-4 mr-2" /> Stop Sharing</> : <><Play className="w-4 h-4 mr-2" /> Start Live Sharing</>}
          </Button>
        </div>
      </div>

      {/* Active trip */}
      {activeTrip && (
        <div className="px-4">
          <p className="text-xs text-gray-500 uppercase mb-2">Active Trip</p>
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-500">{activeTrip.id}</span>
                <Badge className={`border-0 text-[10px] ${statusColor(activeTrip.status)}`}>{activeTrip.status}</Badge>
              </div>
              <p className="font-bold text-[#00A651]">{formatINR(activeTrip.rate)}</p>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1">
                <p className="text-[10px] text-gray-500">FROM</p>
                <p className="font-bold text-sm text-[#0B2545]">{activeTrip.origin.split(',')[0]}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex-1 text-right">
                <p className="text-[10px] text-gray-500">TO</p>
                <p className="font-bold text-sm text-[#0B2545]">{activeTrip.destination.split(',')[0]}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs mb-3">
              <div className="bg-gray-50 rounded p-2"><p className="text-gray-500">Material</p><p className="font-semibold">{activeTrip.material}</p></div>
              <div className="bg-gray-50 rounded p-2"><p className="text-gray-500">Weight</p><p className="font-semibold">{activeTrip.weight}</p></div>
              <div className="bg-gray-50 rounded p-2"><p className="text-gray-500">Vehicle</p><p className="font-semibold font-mono text-[10px]">{activeTrip.vehicleNumber}</p></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button size="sm" variant="outline" className="h-10" onClick={() => setStatusDialog({ open: true, load: activeTrip, newStatus: 'loading' })}>
                <Package className="w-3 h-3 mr-1" />Loading
              </Button>
              <Button size="sm" className="h-10 bg-[#00A651] hover:bg-[#008a43] text-white" onClick={() => setStatusDialog({ open: true, load: activeTrip, newStatus: 'in_transit' })}>
                <Navigation className="w-3 h-3 mr-1" />Start Trip
              </Button>
              <Button size="sm" className="h-10 bg-[#0B2545] hover:bg-[#13315C] text-white" onClick={() => setStatusDialog({ open: true, load: activeTrip, newStatus: 'delivered' })}>
                <CheckCircle2 className="w-3 h-3 mr-1" />Deliver
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Button size="sm" variant="outline" className="h-9" onClick={() => setChatOpen(true)}><MessageCircle className="w-3 h-3 mr-1" />Chat with Shipper</Button>
              <Button size="sm" variant="outline" className="h-9" onClick={() => setPodOpen(true)}><Camera className="w-3 h-3 mr-1" />Upload PoD</Button>
            </div>
          </div>
        </div>
      )}

      {/* My trips */}
      <div className="px-4 mt-5">
        <p className="text-xs text-gray-500 uppercase mb-2">My Trips</p>
        <Tabs defaultValue="active">
          <TabsList className="bg-white border border-gray-100 w-full">
            <TabsTrigger value="active" className="flex-1">Active</TabsTrigger>
            <TabsTrigger value="completed" className="flex-1">Completed</TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="mt-3 space-y-2">
            {trips.filter(t => t.status !== 'Delivered').map(t => (
              <div key={t.id} onClick={() => setActiveTrip(t)} className={`bg-white rounded-xl border p-3 cursor-pointer transition-colors ${activeTrip?.id === t.id ? 'border-[#00A651] bg-[#00A651]/5' : 'border-gray-100 hover:border-gray-200'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[10px] text-gray-500">{t.id}</span>
                  <Badge className={`border-0 text-[10px] ${statusColor(t.status)}`}>{t.status}</Badge>
                </div>
                <div className="flex items-center gap-1 text-sm font-semibold text-[#0B2545]">
                  {t.origin.split(',')[0]} <ArrowRight className="w-3 h-3 text-gray-400" /> {t.destination.split(',')[0]}
                </div>
                <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                  <span>{t.material} • {t.weight}</span>
                  <span className="font-bold text-[#00A651]">{formatINR(t.rate)}</span>
                </div>
              </div>
            ))}
          </TabsContent>
          <TabsContent value="completed" className="mt-3 space-y-2">
            {trips.filter(t => t.status === 'Delivered').map(t => (
              <div key={t.id} className="bg-white rounded-xl border border-gray-100 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[10px] text-gray-500">{t.id}</span>
                  <Badge className="bg-blue-50 text-blue-600 border-0 text-[10px]">Delivered</Badge>
                </div>
                <div className="flex items-center gap-1 text-sm font-semibold">{t.origin.split(',')[0]} → {t.destination.split(',')[0]}</div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-30">
        <div className="grid grid-cols-3 max-w-md mx-auto">
          <button className="py-3 flex flex-col items-center gap-1 text-[#00A651]"><Truck className="w-5 h-5" /><span className="text-[10px] font-semibold">Trips</span></button>
          <button onClick={() => setEarningsOpen(true)} className="py-3 flex flex-col items-center gap-1 text-gray-500"><IndianRupee className="w-5 h-5" /><span className="text-[10px]">Earnings</span></button>
          <button onClick={() => navigate('/app/profile')} className="py-3 flex flex-col items-center gap-1 text-gray-500"><User className="w-5 h-5" /><span className="text-[10px]">Profile</span></button>
        </div>
      </div>

      {/* Status update dialog */}
      <Dialog open={statusDialog.open} onOpenChange={(o) => setStatusDialog({ ...statusDialog, open: o })}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Update Trip Status</DialogTitle></DialogHeader>
          <div className="py-2">
            <p className="text-sm text-gray-600 mb-3">Mark <span className="font-mono font-semibold">{statusDialog.load?.id}</span> as <span className="font-semibold text-[#00A651]">{statusDialog.newStatus?.replace('_', ' ').toUpperCase()}</span>?</p>
            <Textarea placeholder="Add a note (optional)" value={note} onChange={e => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog({ open: false, load: null, newStatus: '' })}>Cancel</Button>
            <Button onClick={updateStatus} className="bg-[#00A651] hover:bg-[#008a43] text-white">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PodUploadDialog open={podOpen} onClose={() => setPodOpen(false)} load={activeTrip} onSuccess={() => {
        setTrips(trips.map(t => t.id === activeTrip.id ? { ...t, status: 'Delivered', progress: 100 } : t));
        // Auto-open return load match after delivery
        setDeliveredLoad(activeTrip);
        setTimeout(() => setReturnMatchOpen(true), 800);
      }} />
      <DriverEarnings open={earningsOpen} onClose={() => setEarningsOpen(false)} />
      <ReturnLoadMatch open={returnMatchOpen} onClose={() => setReturnMatchOpen(false)} completedLoad={deliveredLoad} />
      <LoadChat open={chatOpen} onClose={() => setChatOpen(false)} load={activeTrip} />
    </div>
  );
}
